import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Hash, X, Check } from 'lucide-react';

const EngravingSelector = ({ value, onChange, itemId, selectedColor, selectedForme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [selectedRIA, setSelectedRIA] = useState([]);
  const [selectedPOSTE, setSelectedPOSTE] = useState([]);
  const [selectedPOINTF, setSelectedPOINTF] = useState([]);
  const [selectedSAG, setSelectedSAG] = useState([]);
  const [selectedIPE, setSelectedIPE] = useState([]);
  const [selectedAUTRE, setSelectedAUTRE] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [activeTab, setActiveTab] = useState('numbers');
  
  // Ref pour stocker la dernière valeur traitée lors de l'ouverture du dialog
  const lastParsedValueRef = useRef('');

  // Textes prédéfinis pour l'onglet AUTRE (classés par ordre alphabétique)
  // Note: SAG et IPE sont maintenant dans leurs propres onglets
  const autreOptions = [
    'ARMOIRE GENERALE',
    'ARMOIRE POMPE JOCKEY',
    'ARMOIRE SKID RIA',
    'ARMOIRE SOURCE A',
    'ARMOIRE SOURCE B',
    'ARMOIRE SOURCE B1',
    'ARMOIRE SOURCE B2',
    'ASSERVISSEMENT AEROTHERME',
    'ASSERVISSEMENT PORTE COUPE FEUX',
    'ASSERVISSEMENT TD',
    'ASSERVISSEMENT TELESURVEILLANCE',
    'ASSERVISSEMENT VANNE MARTELIERE',
    'BAC DE RETENTION EMULSEUR',
    'BAC DE RETENTION GNR',
    'BOUCHE D INCENDIE',
    'CLAPET DE NON-RETOUR EMULSEUR',
    'CLAPET DE PIED CREPINE',
    'CODE AAAA',
    'CODE ABCD',
    'CODE BBBB',
    'CODE CCCC',
    'CODE DCBA',
    'CODE DDDD',
    'COLLECTEUR D ESSAIS POMPIERS',
    'COMPRESSEUR D AIR DE MAINTIEN',
    'CONTACT ARRET D URGENCE',
    'DEBIMETRE',
    'DEPART RIA',
    'ELECTROVANNE DE REMPLISSAGE',
    'ESSAI CLOCHE',
    'FIREDOS',
    'FLOTTEUR DE REMPLISSAGE',
    'GONG D ALARME HYDRAULIQUE',
    'GROUPE ELECTRO-POMPE',
    'GROUPE MOTO-POMPE DIESEL',
    'INDICATEUR DE NIVEAU BAS RESERVE',
    'INDICATEUR DE PASSAGE D EAU',
    'LOCAL POSTE SPRINKLER',
    'LOCAL SOURCE SPRINKLER',
    'MANOMETRE AMONT CLAPET',
    'MANOMETRE AVAL CLAPET',
    'MANOMETRE PRESSION D AIR',
    'POMPE DE RELEVAGE',
    'POMPE JOCKEY',
    'POSTE A AIR',
    'POSTE DE CONTROLE PRE-ACTION',
    'POSTE DE CONTROLE SOUS AIR',
    'POSTE DE CONTROLE SOUS EAU',
    'POSTE DELUGE',
    'POTEAU D INCENDIE',
    'PRESSOSTAT BASSE PRESSION AIR',
    'PRESSOSTAT D ALARME FEU',
    'PRESSOSTAT DE DEMARRAGE POMPE JOCKEY',
    'PRESSOSTAT DE DEMARRAGE POMPE PRINCIPALE',
    'PRESSOSTAT HAUTE PRESSION AIR',
    'RESERVE D EAU INCENDIE',
    'RIA SOUS AIR',
    'ROBINET D ISOLEMENT MANOMETRE',
    'ROBINET DE PURGE POINT HAUT',
    'SKID RIA',
    'SOURCE A',
    'SOURCE B',
    'SOURCE B1',
    'SOURCE B2',
    'TABLEAU ALARME SPRINKLER',
    'TRANSMETTEUR TELEPHONIQUE',
    'VANNE BUREAU',
    'VANNE CHAMBRE FROIDE',
    'VANNE CUVE A',
    'VANNE CUVE B1',
    'VANNE CUVE B2',
    'VANNE D ASPIRATION POMPE',
    'VANNE DE BY-PASS',
    'VANNE DE REFOULEMENT POMPE',
    'VANNE DE VIDANGE GENERALE',
    'VANNE DEBITMETRE AMONT',
    'VANNE INBAL',
    'VANNE MEZZANINE',
    'VANNE QUAI DE LIVRAISON',
    'VANNE RACK',
    'VANNE REMPLISSAGE GNR',
    'VANNE TEST DEBITMETRE AVAL',
    'VANNE TGBT',
    'VIDANGE POSTE'
  ];

  // Items avec saisie libre (COLONNE SECHE, CUVE AFFF, CUVE GLYCOLE, CUVE GNR, etc.)
  const autreOptionsWithInput = [
    'ASSERVISSEMENT',
    'CODE',
    'COLONNE SECHE',
    'CUVE AFFF',
    'CUVE GLYCOLE',
    'CUVE GNR',
    'REMPLISSAGE AUTOMATIQUE',
    'VANNE'
  ];
  
  // Placeholders personnalisés pour certains items
  const customPlaceholders = {
    'REMPLISSAGE AUTOMATIQUE': 'L/min m³/H',
    'ASSERVISSEMENT': 'Ex: AEROTHERME, VANNE, TD...',
    'CODE': 'Ex: 1234, 4321...',
    'VANNE': 'Ex: SECTIONNEMENT, ESSAIS, CARBURANT, AFFF, GLYCOLE...'
  };
  
  // État pour les champs de saisie personnalisés
  const [customInputs, setCustomInputs] = useState({});

  // Color mapping
  const getSelectionColor = () => {
    const colorMap = {
      'Rouge': '#dc2626',
      'Bleu': '#2563eb', 
      'Vert': '#16a34a',
      'Noir': '#111827',
      'Blanc': '#ffffff',
      'Jaune': '#ca8a04',
      'Gris': '#475569',
      'Orange': '#ea580c',
      'Violet': '#7c3aed'
    };
    return colorMap[selectedColor] || '#10b981'; // Default emerald
  };

  // Determine available tabs based on forme
  const getAvailableTabs = () => {
    if (!selectedForme) return []; // Aucune forme sélectionnée
    
    if (selectedForme === 'Carré' || selectedForme === 'Rond') {
      return ['numbers', 'letters']; // Seulement nombres et lettres
    } else if (selectedForme === 'Rectangle' || selectedForme === 'Ovale') {
      return ['numbers', 'letters', 'ria', 'poste', 'pointf', 'sag', 'ipe', 'autre']; // Tous les onglets
    }
    
    return []; // Par défaut, rien
  };

  const availableTabs = getAvailableTabs();

  // Ajuster l'onglet actif si nécessaire
  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]); // Passer au premier onglet disponible
    }
  }, [availableTabs, activeTab]);

  // Parse existing value UNIQUEMENT quand le dialog s'ouvre
  // C'est la clé pour éviter le cumul des quantités!
  React.useEffect(() => {
    if (isOpen && value !== lastParsedValueRef.current) {
      // Marquer cette valeur comme traitée
      lastParsedValueRef.current = value;
      
      const items = value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
      
      // Pour chaque catégorie, extraire les items UNIQUES et compter les quantités
      const numbers = items.filter(item => /^\d+$/.test(item));
      const uniqueNumbers = [...new Set(numbers)];
      setSelectedNumbers(uniqueNumbers);
      
      // Calculer les quantités pour TOUS les items
      const newQty = {};
      numbers.forEach(num => {
        newQty[num] = (newQty[num] || 0) + 1;
      });
      
      const letters = items.filter(item => /^[A-Z]$/i.test(item));
      const uniqueLetters = [...new Set(letters.map(l => l.toUpperCase()))];
      setSelectedLetters(uniqueLetters);
      letters.forEach(letter => {
        newQty[letter.toUpperCase()] = (newQty[letter.toUpperCase()] || 0) + 1;
      });
      
      const ria = items.filter(item => /^RIA N°\d+$/i.test(item));
      const uniqueRIA = [...new Set(ria)];
      setSelectedRIA(uniqueRIA);
      ria.forEach(r => {
        newQty[r] = (newQty[r] || 0) + 1;
      });
      
      const poste = items.filter(item => /^POSTE N°\d+$/i.test(item));
      const uniquePOSTE = [...new Set(poste)];
      setSelectedPOSTE(uniquePOSTE);
      poste.forEach(p => {
        newQty[p] = (newQty[p] || 0) + 1;
      });
      
      const pointf = items.filter(item => /^POINT F POSTE N°\d+$/i.test(item));
      const uniquePOINTF = [...new Set(pointf)];
      setSelectedPOINTF(uniquePOINTF);
      pointf.forEach(p => {
        newQty[p] = (newQty[p] || 0) + 1;
      });
      
      const sag = items.filter(item => /^SAG N°\d+$/i.test(item));
      const uniqueSAG = [...new Set(sag)];
      setSelectedSAG(uniqueSAG);
      sag.forEach(s => {
        newQty[s] = (newQty[s] || 0) + 1;
      });
      
      const ipe = items.filter(item => /^IPE N°\d+$/i.test(item));
      const uniqueIPE = [...new Set(ipe)];
      setSelectedIPE(uniqueIPE);
      ipe.forEach(i => {
        newQty[i] = (newQty[i] || 0) + 1;
      });
      
      const autre = items.filter(item => autreOptions.includes(item) || autreOptionsWithInput.some(base => item.startsWith(base)));
      const uniqueAUTRE = [...new Set(autre)];
      setSelectedAUTRE(uniqueAUTRE);
      autre.forEach(a => {
        newQty[a] = (newQty[a] || 0) + 1;
      });
      
      // Mettre à jour toutes les quantités
      setQuantities(newQty);
    }
    
    // Reset le ref quand le dialog se ferme pour permettre la réinitialisation à la prochaine ouverture
    if (!isOpen) {
      lastParsedValueRef.current = '';
    }
  }, [isOpen, value, autreOptions, autreOptionsWithInput]);

  const updateQuantity = (item, qty) => {
    const quantity = Math.max(1, parseInt(qty) || 1);
    setQuantities(prev => ({...prev, [item]: quantity}));
  };

  const toggleNumber = (num) => {
    const numStr = num.toString();
    setSelectedNumbers(prev => 
      prev.includes(numStr) 
        ? prev.filter(n => n !== numStr)
        : [...prev, numStr].sort((a, b) => parseInt(a) - parseInt(b))
    );
  };

  const toggleLetter = (letter) => {
    setSelectedLetters(prev => 
      prev.includes(letter)
        ? prev.filter(l => l !== letter)
        : [...prev, letter].sort()
    );
  };

  const toggleRIA = (num) => {
    const riaStr = `RIA N°${num}`;
    setSelectedRIA(prev => 
      prev.includes(riaStr) 
        ? prev.filter(r => r !== riaStr)
        : [...prev, riaStr].sort((a, b) => {
            const numA = parseInt(a.replace('RIA N°', ''));
            const numB = parseInt(b.replace('RIA N°', ''));
            return numA - numB;
          })
    );
  };

  const togglePOSTE = (num) => {
    const posteStr = `POSTE N°${num}`;
    setSelectedPOSTE(prev => 
      prev.includes(posteStr) 
        ? prev.filter(p => p !== posteStr)
        : [...prev, posteStr].sort((a, b) => {
            const numA = parseInt(a.replace('POSTE N°', ''));
            const numB = parseInt(b.replace('POSTE N°', ''));
            return numA - numB;
          })
    );
  };

  const togglePOINTF = (num) => {
    const pointfStr = `POINT F POSTE N°${num}`;
    setSelectedPOINTF(prev => 
      prev.includes(pointfStr) 
        ? prev.filter(p => p !== pointfStr)
        : [...prev, pointfStr].sort((a, b) => {
            const numA = parseInt(a.replace('POINT F POSTE N°', ''));
            const numB = parseInt(b.replace('POINT F POSTE N°', ''));
            return numA - numB;
          })
    );
  };

  const toggleSAG = (num) => {
    const sagStr = `SAG N°${num}`;
    setSelectedSAG(prev => 
      prev.includes(sagStr) 
        ? prev.filter(s => s !== sagStr)
        : [...prev, sagStr].sort((a, b) => {
            const numA = parseInt(a.replace('SAG N°', ''));
            const numB = parseInt(b.replace('SAG N°', ''));
            return numA - numB;
          })
    );
  };

  const toggleIPE = (num) => {
    const ipeStr = `IPE N°${num}`;
    setSelectedIPE(prev => 
      prev.includes(ipeStr) 
        ? prev.filter(i => i !== ipeStr)
        : [...prev, ipeStr].sort((a, b) => {
            const numA = parseInt(a.replace('IPE N°', ''));
            const numB = parseInt(b.replace('IPE N°', ''));
            return numA - numB;
          })
    );
  };

  const toggleAUTRE = (text) => {
    setSelectedAUTRE(prev => 
      prev.includes(text) 
        ? prev.filter(t => t !== text)
        : [...prev, text]
    );
  };

  const selectAllNumbers = () => {
    const allNumbers = Array.from({ length: 100 }, (_, i) => (i + 1).toString());
    setSelectedNumbers(allNumbers);
  };

  const selectAllLetters = () => {
    const allLetters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    setSelectedLetters(allLetters);
  };

  const selectAllRIA = () => {
    const allRIA = Array.from({ length: 100 }, (_, i) => `RIA N°${i + 1}`);
    setSelectedRIA(allRIA);
  };

  const selectAllPOSTE = () => {
    const allPOSTE = Array.from({ length: 100 }, (_, i) => `POSTE N°${i + 1}`);
    setSelectedPOSTE(allPOSTE);
  };

  const selectAllPOINTF = () => {
    const allPOINTF = Array.from({ length: 100 }, (_, i) => `POINT F POSTE N°${i + 1}`);
    setSelectedPOINTF(allPOINTF);
  };

  const selectAllSAG = () => {
    const allSAG = Array.from({ length: 10 }, (_, i) => `SAG N°${i + 1}`);
    setSelectedSAG(allSAG);
  };

  const selectAllIPE = () => {
    const allIPE = Array.from({ length: 10 }, (_, i) => `IPE N°${i + 1}`);
    setSelectedIPE(allIPE);
  };

  const selectAllAUTRE = () => {
    setSelectedAUTRE([...autreOptions]);
  };

  const clearNumbers = () => setSelectedNumbers([]);
  const clearLetters = () => setSelectedLetters([]);
  const clearRIA = () => setSelectedRIA([]);
  const clearPOSTE = () => setSelectedPOSTE([]);
  const clearPOINTF = () => setSelectedPOINTF([]);
  const clearSAG = () => setSelectedSAG([]);
  const clearIPE = () => setSelectedIPE([]);
  const clearAUTRE = () => setSelectedAUTRE([]);

  // NOUVELLE fonction : appliquer la sélection d'un onglet spécifique
  const applyTabSelection = (tab) => {
    // Récupérer la valeur actuelle
    const currentItems = value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    let combined = [...currentItems];
    
    // Fonction pour enlever les anciens éléments d'une catégorie
    const removeOldCategory = (category) => {
      if (category === 'numbers') {
        combined = combined.filter(item => !/^\d+$/.test(item));
      } else if (category === 'letters') {
        combined = combined.filter(item => !/^[A-Z]$/i.test(item));
      } else if (category === 'ria') {
        combined = combined.filter(item => !/^RIA N°\d+$/i.test(item));
      } else if (category === 'poste') {
        combined = combined.filter(item => !/^POSTE N°\d+$/i.test(item));
      } else if (category === 'pointf') {
        combined = combined.filter(item => !/^POINT F POSTE N°\d+$/i.test(item));
      } else if (category === 'sag') {
        combined = combined.filter(item => !/^SAG N°\d+$/i.test(item));
      } else if (category === 'ipe') {
        combined = combined.filter(item => !/^IPE N°\d+$/i.test(item));
      } else if (category === 'autre') {
        // Enlever tous les items AUTRE : standards + items avec saisie personnalisée
        combined = combined.filter(item => {
          // Vérifier si c'est un item standard
          if (autreOptions.includes(item)) return false;
          
          // Vérifier si c'est un item avec saisie personnalisée (commence par COLONNE SECHE, CUVE AFFF, etc.)
          for (const baseText of autreOptionsWithInput) {
            if (item.startsWith(baseText)) return false;
          }
          
          return true; // Garder les autres items
        });
      }
    };

    // Enlever les anciens éléments de cette catégorie
    removeOldCategory(tab);

    // Ajouter les nouveaux éléments selon l'onglet
    if (tab === 'numbers') {
      selectedNumbers.forEach(num => {
        const qty = quantities[num] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(num);
        }
      });
    } else if (tab === 'letters') {
      selectedLetters.forEach(letter => {
        const qty = quantities[letter] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(letter);
        }
      });
    } else if (tab === 'ria') {
      selectedRIA.forEach(ria => {
        const qty = quantities[ria] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(ria);
        }
      });
    } else if (tab === 'poste') {
      selectedPOSTE.forEach(poste => {
        const qty = quantities[poste] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(poste);
        }
      });
    } else if (tab === 'pointf') {
      selectedPOINTF.forEach(pointf => {
        const qty = quantities[pointf] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(pointf);
        }
      });
    } else if (tab === 'sag') {
      selectedSAG.forEach(sag => {
        const qty = quantities[sag] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(sag);
        }
      });
    } else if (tab === 'ipe') {
      selectedIPE.forEach(ipe => {
        const qty = quantities[ipe] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(ipe);
        }
      });
    } else if (tab === 'autre') {
      selectedAUTRE.forEach(text => {
        const qty = quantities[text] || 1;
        for (let i = 0; i < qty; i++) {
          combined.push(text);
        }
      });
    }
    
    const newValue = combined.join(', ');
    
    // IMPORTANT: Marquer cette nouvelle valeur comme déjà traitée 
    // pour éviter que le useEffect ne réinitialise les états
    lastParsedValueRef.current = newValue;
    
    onChange(newValue);
    
    // LA MODALE RESTE OUVERTE - ne pas fermer
  };

  // Fonction pour gérer la molette de souris sur les champs de quantité
  const handleWheelQuantity = (e, itemKey) => {
    e.preventDefault();
    const currentQty = quantities[itemKey] || 1;
    const delta = e.deltaY < 0 ? 1 : -1; // Scroll up = +1, Scroll down = -1
    const newQty = Math.max(1, currentQty + delta);
    updateQuantity(itemKey, newQty);
  };

  const renderNumberGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllNumbers}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearNumbers}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedNumbers.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-8 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 100 }, (_, i) => i + 1).map(num => {
          const numStr = num.toString();
          const isSelected = selectedNumbers.includes(numStr);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={num} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className={`h-7 text-xs flex-1`}
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleNumber(num)}
              >
                {num}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[numStr] || 1}
                  onChange={(e) => updateQuantity(numStr, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, numStr)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('numbers')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider NOMBRE
        </Button>
      </div>
    </div>
  );

  const renderLetterGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllLetters}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearLetters}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedLetters.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-8 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => {
          const isSelected = selectedLetters.includes(letter);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={letter} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className={`h-7 text-xs flex-1`}
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleLetter(letter)}
              >
                {letter}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[letter] || 1}
                  onChange={(e) => updateQuantity(letter, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, letter)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('letters')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider LETTRE
        </Button>
      </div>
    </div>
  );

  const renderRIAGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllRIA}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearRIA}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedRIA.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 100 }, (_, i) => i + 1).map(num => {
          const riaStr = `RIA N°${num}`;
          const isSelected = selectedRIA.includes(riaStr);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={num} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="h-7 text-xs flex-1"
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleRIA(num)}
              >
                RIA N°{num}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[riaStr] || 1}
                  onChange={(e) => updateQuantity(riaStr, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, riaStr)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('ria')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider RIA
        </Button>
      </div>
    </div>
  );

  const renderPOSTEGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllPOSTE}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearPOSTE}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedPOSTE.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 100 }, (_, i) => i + 1).map(num => {
          const posteStr = `POSTE N°${num}`;
          const isSelected = selectedPOSTE.includes(posteStr);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={num} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="h-7 text-xs flex-1"
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => togglePOSTE(num)}
              >
                POSTE N°{num}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[posteStr] || 1}
                  onChange={(e) => updateQuantity(posteStr, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, posteStr)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('poste')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider POSTE
        </Button>
      </div>
    </div>
  );

  const renderPOINTFGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllPOINTF}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearPOINTF}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedPOINTF.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="space-y-2 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1">
          {Array.from({ length: 50 }, (_, i) => i + 1).map(num => {
            const pointfStr = `POINT F POSTE N°${num}`;
            const isSelected = selectedPOINTF.includes(pointfStr);
            const selectionColor = getSelectionColor();
            
            return (
              <div key={num} className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  className="h-7 text-xs flex-1"
                  style={isSelected ? {
                    backgroundColor: selectionColor,
                    borderColor: selectionColor,
                    color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                  } : {}}
                  onClick={() => togglePOINTF(num)}
                >
                  POINT F N°{num}
                </Button>
                {isSelected && (
                  <Input
                    type="number" 
                    min="1"
                    value={quantities[pointfStr] || 1}
                    onChange={(e) => updateQuantity(pointfStr, e.target.value)}
                    onWheel={(e) => handleWheelQuantity(e, pointfStr)}
                    className="h-6 text-xs text-center"
                    placeholder="Qté"
                  />
                )}
              </div>
            );
          })}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1">
          {Array.from({ length: 50 }, (_, i) => i + 51).map(num => {
            const pointfStr = `POINT F POSTE N°${num}`;
            const isSelected = selectedPOINTF.includes(pointfStr);
            const selectionColor = getSelectionColor();
            
            return (
              <div key={num} className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  className="h-7 text-xs flex-1"
                  style={isSelected ? {
                    backgroundColor: selectionColor,
                    borderColor: selectionColor,
                    color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                  } : {}}
                  onClick={() => togglePOINTF(num)}
                >
                  POINT F N°{num}
                </Button>
                {isSelected && (
                  <Input
                    type="number" 
                    min="1"
                    value={quantities[pointfStr] || 1}
                    onChange={(e) => updateQuantity(pointfStr, e.target.value)}
                    onWheel={(e) => handleWheelQuantity(e, pointfStr)}
                    className="h-6 text-xs text-center"
                    placeholder="Qté"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('pointf')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider POINT F
        </Button>
      </div>
    </div>
  );

  const renderSAGGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllSAG}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearSAG}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedSAG.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(num => {
          const sagStr = `SAG N°${num}`;
          const isSelected = selectedSAG.includes(sagStr);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={num} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="h-7 text-xs flex-1"
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleSAG(num)}
              >
                SAG N°{num}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[sagStr] || 1}
                  onChange={(e) => updateQuantity(sagStr, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, sagStr)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('sag')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider SAG
        </Button>
      </div>
    </div>
  );

  const renderIPEGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllIPE}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearIPE}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedIPE.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(num => {
          const ipeStr = `IPE N°${num}`;
          const isSelected = selectedIPE.includes(ipeStr);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={num} className="flex flex-col gap-1">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="h-7 text-xs flex-1"
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleIPE(num)}
              >
                IPE N°{num}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[ipeStr] || 1}
                  onChange={(e) => updateQuantity(ipeStr, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, ipeStr)}
                  className="h-6 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('ipe')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider IPE
        </Button>
      </div>
    </div>
  );

  const renderAUTREGrid = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAllAUTRE}>
            Tout sélectionner
          </Button>
          <Button size="sm" variant="outline" onClick={clearAUTRE}>
            Tout déselectionner
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedAUTRE.length} sélectionné(s)
        </Badge>
      </div>
      
      <div className="space-y-1 min-h-[320px] max-h-80 overflow-y-auto p-2 border rounded-lg">
        {/* Items standards */}
        {autreOptions.map(text => {
          const isSelected = selectedAUTRE.includes(text);
          const selectionColor = getSelectionColor();
          
          return (
            <div key={text} className="flex items-center gap-2">
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="flex-1 h-8 text-xs justify-start"
                style={isSelected ? {
                  backgroundColor: selectionColor,
                  borderColor: selectionColor,
                  color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                } : {}}
                onClick={() => toggleAUTRE(text)}
              >
                {text}
              </Button>
              {isSelected && (
                <Input
                  type="number" 
                  min="1"
                  value={quantities[text] || 1}
                  onChange={(e) => updateQuantity(text, e.target.value)}
                  onWheel={(e) => handleWheelQuantity(e, text)}
                  className="h-8 w-16 text-xs text-center"
                  placeholder="Qté"
                />
              )}
            </div>
          );
        })}
        
        {/* Items avec saisie personnalisée (CUVE GNR et AFFF) */}
        <div className="pt-2 mt-2 border-t">
          <p className="text-xs font-medium text-slate-600 mb-2">Items avec contenance personnalisée :</p>
          
          {autreOptionsWithInput.map(baseText => {
            const customValue = customInputs[baseText] || '';
            const fullText = customValue ? `${baseText} ${customValue}` : baseText;
            const isSelected = selectedAUTRE.some(item => item.startsWith(baseText));
            const selectionColor = getSelectionColor();
            
            return (
              <div key={baseText} className="space-y-2 mb-3 p-2 bg-slate-50 rounded">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={isSelected ? "default" : "outline"}
                    className="flex-1 h-8 text-xs justify-start"
                    style={isSelected ? {
                      backgroundColor: selectionColor,
                      borderColor: selectionColor,
                      color: selectionColor === '#ffffff' ? '#111827' : '#ffffff'
                    } : {}}
                    onClick={() => {
                      // Si on sélectionne, on doit d'abord avoir une valeur personnalisée
                      if (!isSelected && !customValue) {
                        // Juste activer le champ, ne pas sélectionner encore
                        return;
                      }
                      // Retirer toutes les variantes de ce baseText
                      const filtered = selectedAUTRE.filter(item => !item.startsWith(baseText));
                      if (isSelected) {
                        setSelectedAUTRE(filtered);
                      } else {
                        setSelectedAUTRE([...filtered, fullText]);
                      }
                    }}
                  >
                    {fullText}
                  </Button>
                  {isSelected && (
                    <Input
                      type="number" 
                      min="1"
                      value={quantities[fullText] || 1}
                      onChange={(e) => updateQuantity(fullText, e.target.value)}
                      onWheel={(e) => handleWheelQuantity(e, fullText)}
                      className="h-8 w-16 text-xs text-center"
                      placeholder="Qté"
                    />
                  )}
                </div>
                <Input
                  type="text"
                  value={customValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setCustomInputs(prev => ({...prev, [baseText]: newValue}));
                    
                    // Auto-sélection : dès qu'on tape, on sélectionne l'item avec quantité 1
                    const filtered = selectedAUTRE.filter(item => !item.startsWith(baseText));
                    if (newValue) {
                      // Sélectionner automatiquement avec la nouvelle valeur
                      setSelectedAUTRE([...filtered, `${baseText} ${newValue}`]);
                      // Initialiser la quantité à 1 si pas déjà définie
                      const fullTextKey = `${baseText} ${newValue}`;
                      if (!quantities[fullTextKey]) {
                        setQuantities(prev => ({...prev, [fullTextKey]: 1}));
                      }
                    } else {
                      // Si le champ est vidé, désélectionner l'item
                      setSelectedAUTRE(filtered);
                    }
                  }}
                  className="h-8 text-xs"
                  placeholder={
                    customPlaceholders[baseText] 
                      ? customPlaceholders[baseText]
                      : baseText === 'COLONNE SECHE' 
                        ? 'Ex: DN100, DN150 Q500, DN100 DEBIT 500L/MIN...' 
                        : 'Ex: 500L, 1000L, 2000L...'
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bouton Valider pour cet onglet */}
      <div className="flex justify-end pt-2">
        <Button onClick={() => applyTabSelection('autre')} className="bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4 mr-2" />
          Valider AUTRE
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="w-10 h-10 p-0 border-2 border-emerald-600 hover:bg-emerald-50 transition-colors"
        >
          <Hash className="h-5 w-5 text-emerald-600" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sélectionner les gravures</DialogTitle>
        </DialogHeader>
        
        {availableTabs.length === 0 ? (
          // Aucune forme sélectionnée - Afficher le message
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-center space-y-4">
              <div className="text-6xl">⚠️</div>
              <h3 className="text-xl font-semibold text-slate-700">
                Veuillez d&apos;abord sélectionner une forme
              </h3>
              <p className="text-slate-500">
                Choisissez une forme (Rond, Carré, Rectangle ou Ovale) dans le tableau avant de sélectionner des gravures.
              </p>
              <Button variant="outline" onClick={() => setIsOpen(false)} className="mt-4">
                <X className="h-4 w-4 mr-2" />
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          // Forme sélectionnée - Afficher les onglets
          <>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)` }}>
            {availableTabs.includes('numbers') && <TabsTrigger value="numbers">NOMBRE</TabsTrigger>}
            {availableTabs.includes('letters') && <TabsTrigger value="letters">LETTRE</TabsTrigger>}
            {availableTabs.includes('ria') && <TabsTrigger value="ria">RIA</TabsTrigger>}
            {availableTabs.includes('poste') && <TabsTrigger value="poste">POSTE</TabsTrigger>}
            {availableTabs.includes('pointf') && <TabsTrigger value="pointf">POINT F</TabsTrigger>}
            {availableTabs.includes('sag') && <TabsTrigger value="sag">SAG</TabsTrigger>}
            {availableTabs.includes('ipe') && <TabsTrigger value="ipe">IPE</TabsTrigger>}
            {availableTabs.includes('autre') && <TabsTrigger value="autre">AUTRE</TabsTrigger>}
          </TabsList>
          
          <TabsContent value="numbers" className="mt-4">
            {renderNumberGrid()}
          </TabsContent>
          
          <TabsContent value="letters" className="mt-4">
            {renderLetterGrid()}
          </TabsContent>
          
          <TabsContent value="ria" className="mt-4">
            {renderRIAGrid()}
          </TabsContent>
          
          <TabsContent value="poste" className="mt-4">
            {renderPOSTEGrid()}
          </TabsContent>
          
          <TabsContent value="pointf" className="mt-4">
            {renderPOINTFGrid()}
          </TabsContent>
          
          <TabsContent value="sag" className="mt-4">
            {renderSAGGrid()}
          </TabsContent>
          
          <TabsContent value="ipe" className="mt-4">
            {renderIPEGrid()}
          </TabsContent>
          
          <TabsContent value="autre" className="mt-4">
            {renderAUTREGrid()}
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-slate-600">
            Sélections actuelles: {value || 'Aucune'}
          </div>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            <X className="h-4 w-4 mr-2" />
            Fermer
          </Button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EngravingSelector;
